import { ApiProperty } from '@nestjs/swagger';
import { Expose, Type } from 'class-transformer';
import { IsBoolean, IsDateString, IsInt, IsNumber, IsOptional, IsString, Max, Min, ValidateNested } from 'class-validator';
import type { ApiServerEntityIDType } from '../../types';
import { CHILD_MAX_AGE, CHILD_MIN_AGE } from '../../const/common';
import { ClientProfileViewModel } from '../client';
import { ClientPhotoViewModel } from '../client/photo/view';
import { ImageAsset } from '../resource/image';
import { DealStatus } from './const';

export interface DealChildView {
  age: number;
}

export interface DealView {
  id: ApiServerEntityIDType;
  status: DealStatus;
  startedAt: string;
  endedAt: string;
  price: number;
  oldPrice?: number;
  children?: DealChildView[];
  childWithDisabilities: boolean;
  sickChild: boolean;
  description: string;
  address: string;
  location: string;
  createdAt: Date;
  updatedAt: Date;
  subjectProviderClientId: number;
  subjectConsumerClientId: number;
}

export class DealClientDetailsViewModel {
  @Expose()
  @ApiProperty({
    example: 'John',
    description: "Client's name",
    required: false,
  })
  name?: string;

  @Expose()
  @ApiProperty({
    required: false,
    example: 'Smyth',
    default: "Client's surname(last name)",
  })
  surname?: string;
}

export class DealClientView {
  @Expose()
  @Type(() => ClientProfileViewModel)
  @ApiProperty({
    required: true,
    examples: {
      name: 'John',
      surname: 'Smyth',
    },
    description: "Client's details",
    type: ClientProfileViewModel,
  })
  details: ClientProfileViewModel;

  @Expose()
  @Type(() => ClientPhotoViewModel)
  @ApiProperty({
    required: true,
    type: ImageAsset,
  })
  photo?: ImageAsset;
}

export class DealChildViewModel {
  @Expose()
  @ApiProperty({
    description: `Child's age (between ${CHILD_MIN_AGE} and ${CHILD_MAX_AGE})`,
    example: 7,
  })
  @IsInt()
  @Min(CHILD_MIN_AGE)
  @Max(CHILD_MAX_AGE)
  age: number;
}

export class DealViewModel {
  @Expose()
  @ApiProperty({ description: 'Unique deal ID', example: 1001 })
  @IsNumber()
  id: ApiServerEntityIDType;

  @Expose()
  @ApiProperty({ enum: DealStatus, enumName: 'DealStatus' })
  status: DealStatus;

  @Expose()
  @ApiProperty({ description: 'Start date (ISO)', example: '2025-09-01T09:00:00.000Z' })
  @IsDateString()
  startedAt: string;

  @Expose()
  @ApiProperty({ description: 'End date (ISO)', example: '2025-09-03T18:00:00.000Z' })
  @IsDateString()
  endedAt: string;

  @Expose()
  @ApiProperty({ description: 'Agreed price', example: 15000 })
  @IsNumber()
  price: number;

  @Expose()
  @ApiProperty({ description: 'Previous price (if updated)', example: 17000, required: false })
  @IsOptional()
  @IsNumber()
  oldPrice?: number;

  @Expose()
  @ApiProperty({ type: DealChildViewModel, isArray: true, required: false })
  @ValidateNested({ each: true })
  @Type(() => DealChildViewModel)
  @IsOptional()
  children?: DealChildViewModel[];

  @Expose()
  @ApiProperty({ description: 'Whether any child has disabilities', example: false })
  @IsBoolean()
  childWithDisabilities: boolean;

  @Expose()
  @ApiProperty({ description: 'Whether any child is sick', example: true })
  @IsBoolean()
  sickChild: boolean;

  @Expose()
  @ApiProperty({ description: 'Deal description', example: 'Weekend care for two children' })
  @IsString()
  description: string;

  @Expose()
  @ApiProperty({ description: 'Full address', example: '123 Main St, Moscow' })
  @IsString()
  address: string;

  @Expose()
  @ApiProperty({ description: 'Location or region', example: '53.337559,83.658512' })
  @IsString()
  location: string;

  @Expose()
  @ApiProperty({ description: 'Creation timestamp', example: '2025-08-30T12:00:00.000Z' })
  @IsDateString()
  createdAt: Date;

  @Expose()
  @ApiProperty({ description: 'Last update timestamp', example: '2025-09-01T08:00:00.000Z' })
  @IsDateString()
  updatedAt: Date;

  /**
   * ISO date string until which employer invite timer is active.
   * When present, client should show the invite timer banner and count down to this timestamp.
   */
  @Expose()
  @ApiProperty({ required: false, type: String, format: 'date-time' })
  @Type(() => Date)
  @IsOptional()
  inviteTimerEndsAt?: Date | string;

  @Expose()
  @ApiProperty({ description: 'Client ID of the provider', example: 2001 })
  @IsNumber()
  subjectProviderClientId: ApiServerEntityIDType;

  @Expose()
  @ApiProperty({ type: DealClientView })
  @ValidateNested({ each: true })
  @Type(() => DealClientView)
  @IsOptional()
  subjectProvider?: DealClientView;

  @Expose()
  @ApiProperty({ type: DealClientView })
  @ValidateNested({ each: true })
  @Type(() => DealClientView)
  @IsOptional()
  subjectConsumer?: DealClientView;

  @Expose()
  @ApiProperty({ description: 'Client ID of the consumer', example: 3002 })
  @IsNumber()
  subjectConsumerClientId: ApiServerEntityIDType;

  @Expose()
  @IsBoolean()
  @ApiProperty({ description: 'The deal was created from the vacancy', example: false })
  isCreatedFromVacancy: boolean;

  @Expose()
  @IsNumber()
  @ApiProperty({ description: 'The ID of the client who initiated the transaction', example: 2001 })
  initiatorClientId?: ApiServerEntityIDType;

  @Expose()
  @IsNumber()
  @ApiProperty({ description: 'The Id of the references dialog' })
  dialogId: ApiServerEntityIDType;

  @Expose()
  @IsOptional()
  rejectionReasonDescription?: string;
}
